<script lang="ts" setup>
import WidgetPanels from "@/components/widget/WidgetPanels.vue";
import CommonFilter from "@/components/common/CommonFilter.vue";
import CommonTable from "@/components/common/CommonTable.vue";
import {ITableHeader} from "@/types/table";
import {useGroupStore} from "@/features/group/group.store";
import {onMounted, ref, watch} from "vue";
import {useAppStore} from "@/store/app.store";
import {IChat} from "@/features/chat/chat.types";
import router from "@/router";
import {IPaginationMeta} from "@/features/pagination/pagination.types";
import CommonPagination from "@/components/common/CommonPagination.vue";

const groupStore = useGroupStore();
const appStore = useAppStore();

const loading = ref<boolean>(false);
const list = ref<IChat>();
const isFetching = ref<boolean>(false)
const paginationData = ref<IPaginationMeta>()

const getChats = async () => {
  if (isFetching.value) return

  try {
    isFetching.value = true
    list.value = await groupStore.getChats(loading)
    if (list.value) {
      paginationData.value = {
        has_more: list.value.has_more,
        next_page: list.value.next_page,
        page_size: list.value.page_size,
        page: list.value.page,
        pages: list.value.pages,
        prev_page: list.value.prev_page,
        total: list.value.total,
      }
    }

  } finally {
    isFetching.value = false
  }
}

const handlePageChange = (page: number) => {
  groupStore.commonFilter.page = page;
}

const handleLimitChange = (limit: number) => {
  groupStore.commonFilter.page = 1;
  groupStore.commonFilter.page_size = limit;
}

const handleRowClicked = (row: any, rowIndex: number) => {
  router.push(`/group/${row.id}`);
}

onMounted(() => {
})

watch(
    [
      () => appStore.refreshKey,
      () => JSON.stringify(groupStore.commonFilter)
    ],
    () => {
      getChats()
    },
    {
      immediate: true,
    }
)
</script>

<template>
  <div class="v-group">
    <WidgetPanels type="groups" :filter="groupStore.commonFilter"/>

    <div class="body">
      <CommonFilter
          :with-shift="false"
          :with-date="false"
          :with-outgoing="false"
          :loading="loading"
          :store="groupStore"
      />

      <CommonTable
          :headers="groupStore.headers"
          :items="list?.items"
          :loading="loading"
          @row-clicked="handleRowClicked"
      >
        <template #id="{index}">
          {{ (list.page - 1) * list.page_size + index + 1 }}
        </template>

        <template #all="{item}">
          {{ item.messages.total }}
        </template>

        <template #text="{item}">
          {{ item.messages.text }}
        </template>

        <template #voice="{item}">
          {{ item.messages.voice + item.messages.document + item.messages.audio }}
        </template>

        <template #call="{item}">
          {{ item.messages.calls }}
        </template>

        <template #photo="{item}">
          {{ item.messages.photo + item.messages.video + item.messages.round }}
        </template>

        <template #all_errors="{item}">
          {{ item.errors.total }}
        </template>

        <template #reply_time="{item}">
          {{ item.errors.reply_time_exceed }}
        </template>

        <template #unanswered="{item}">
          {{ item.errors.missed_calls + item.errors.declined_calls }}
        </template>

        <template #deleted="{item}">
          {{ item.errors.deleted }}
        </template>

        <template #edited="{item}">
          {{ item.errors.edited }}
        </template>
      </CommonTable>
    </div>
    <CommonPagination
        v-if="list && list.items && list.items.length"
        :meta="paginationData"
        @change-page="handlePageChange"
        @change-limit="handleLimitChange"
    />
  </div>
</template>

<style lang="scss" scoped>
.v-group {
  .body {
    padding: 1rem 1rem 0 1rem;
    height: calc(100vh - 267px);
  }
}
</style>
